import React from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import { Delete } from '@mui/icons-material';

/* eslint-disable @typescript-eslint/ban-types */
type FilterChipsProps<T extends Object> = {
  label: React.ReactNode;
  options: T[];
  multiple?: boolean;
  selected: T[]; // controlled selected values
  onChange: (selected: T[]) => void;
  centered?: boolean;
  onDelete?: (option: T) => void;
  getOptionLabel?: (option: T) => string;
  optionsEqual?: (option1: T, option2: T) => boolean;
  renderOption?: (option: T, isSelected: boolean, onClick: () => void) => React.ReactNode;
};

const FilterChips = <T extends Object>({
  label,
  options,
  multiple = false,
  selected,
  onChange,
  centered = false,
  onDelete,
  getOptionLabel = (option: T) => option.toString(),
  optionsEqual = (option1: T, option2: T) => option1 === option2,
  renderOption,
}: FilterChipsProps<T>) => {
  const handleChipClick = (option: T) => {
    let newSelected: T[];

    if (multiple) {
      newSelected = selected.some(val => optionsEqual(val, option))
        ? selected.filter((val) => !optionsEqual(val, option))
        : [...selected, option];
    } else {
      newSelected = selected.some(val => optionsEqual(val, option)) ? [] : [option];
    }

    onChange(newSelected);
  };

  return (
    <div>
      <Typography textAlign={centered ? 'center' : 'inherit'} gutterBottom>
          <b>{label}</b>
      </Typography>
      <Stack
        justifyContent={centered ? 'center' : 'inherit'}
        direction="row"
        spacing={1}
        flexWrap="wrap"
        alignItems={"center"}
      >
        
        {options.map((option) => {
          const isSelected = selected.some(val => optionsEqual(val, option));
          
          if (renderOption) {
            return (
              <div key={getOptionLabel(option)} style={{margin: 3}}>
                {renderOption(option, isSelected, () => handleChipClick(option))}
              </div>
            );
          }
          
          return (
            <Chip
              key={getOptionLabel(option)}
              label={getOptionLabel(option)}
              clickable
              style={{margin: 3}}
              color={isSelected ? 'primary' : 'default'}
              variant={isSelected ? 'filled' : 'outlined'}
              onClick={() => handleChipClick(option)}
              onDelete={onDelete && ((e) => {
                e.stopPropagation(); // prevent triggering onClick
                if (onDelete) onDelete(option);
              })}
              deleteIcon={onDelete ? <Delete /> : undefined}
            />
          );
        })}
      </Stack>
    </div>
  );
};

export default FilterChips;
